import { get } from "lodash";
import {
    conditionTypes,
    propertyClassTypes,
    propertyQualityTypes,
} from "../../create-property/definitions";
import { yearBuiltOptions } from "../../../utils/helperUtils";

const generateCells = (data: any, pathsWithOptions: any[], size: number) => {
    const subject = pathsWithOptions.map(({ path, options }) =>
        options.withSubject === false
            ? {
                  type: "ignore",
              }
            : {
                  key: `appealDetails.appealApproachCalculationData.sales.subject.${path}`,
                  value: get(
                      data,
                      `appealDetails.appealApproachCalculationData.sales.subject.${path}`,
                      ""
                  ),
                  ...options,
                  imageObjectKey: options.imageObjectKey
                      ? `appealDetails.appealApproachCalculationData.sales.subject.${options.imageObjectKey}`
                      : undefined,
              }
    );

    const comps = Array.from({ length: size }, (_, index) =>
        pathsWithOptions.map(({ path, options }) => ({
            key: `appealDetails.appealApproachCalculationData.sales.comps.${index}.${path}`,
            value: get(
                data,
                `appealDetails.appealApproachCalculationData.sales.comps.${index}.${path}`,
                ""
            ),
            ...options,
            imageObjectKey: options.imageObjectKey
                ? `appealDetails.appealApproachCalculationData.sales.comps.${index}.${options.imageObjectKey}`
                : undefined,
        }))
    );

    return [subject, ...comps];
};

const getToggleValues = (size: number) => {
    return Object.fromEntries(
        Array.from({ length: size }, (_, index) => [
            index + 1,
            `appealDetails.appealApproachCalculationData.sales.comps.${index}.enable`,
        ])
    );
};

export const INITIAL_SALES_APPROACH_TABLE_DATA_DEFINITION = (data: any) => {
    const size = Object.keys(data.appealDetails.appealApproachCalculationData.sales.comps).length;

    return {
        headers: ["Subject", ...Array.from({ length: size }, (_, index) => `Comp #${index + 1}`)],
        toggles: getToggleValues(size),
        sections: [
            {
                title: "Property Characteristics",
                key: "propertyCharacteristics",
                rows: [
                    {
                        label: "Street Address",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "address.streetaddress",
                                    options: {
                                        type: "text",
                                        editable: false,
                                        imageObjectKey: "addressS3Object",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "City, county and State",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "address.city",
                                    options: { type: "inputString", editable: true },
                                },
                                {
                                    path: "address.county",
                                    options: { type: "inputString", editable: true, font: "b100" },
                                },
                                {
                                    path: "address.state",
                                    options: { type: "inputString", editable: true, font: "b100" },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Parcel number",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "parcelNumber",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Building type",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "propertyType",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Use",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "propertyType",
                                    options: {
                                        withSubject: true,
                                        type: "inputString",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Building size (sqft)",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "buildingSizeSqFt",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land size (sqft)",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "landSizeSqFt",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land to building ratio",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "landToBuildingRatio",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Year built (effective)",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "yearBuilt",
                                    options: {
                                        type: "dropdown",
                                        options: [...yearBuiltOptions],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Effective year",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "effectiveYear",
                                    options: {
                                        type: "dropdown",
                                        options: [...yearBuiltOptions],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Rental class",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "rentalClass",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyClassTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Construction Class",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "constructionClass",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyClassTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Quality of construction",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "qualityOfConstruction",
                                    options: {
                                        type: "dropdown",
                                        options: [...propertyQualityTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Condition",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "condition",
                                    options: {
                                        type: "dropdown",
                                        options: [...conditionTypes],
                                        editable: true,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Office %",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "officePercentage",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Average story (clear) height",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "averageStoryHeight",
                                    options: {
                                        withSubject: true,
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: " ft",
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Comments",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "appealComments",
                                    options: { type: "comment", editable: true },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Sale Information",
                key: "saleInformation",
                rows: [
                    {
                        label: "Sale date",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "salesInformation.saleDate",
                                    options: { type: "text", editable: false, withSubject: false },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Occupancy status at sale",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "salesInformation.occupancyStatus",
                                    options: { type: "text", editable: false, withSubject: false },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Sale price",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "salesInformation.salePrice",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "salesInformation.salePricePerSqFt",
                                    options: {
                                        type: "text",
                                        editable: false,
                                        font: "b100",
                                        suffix: "/sqft",
                                        prefix: "$",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Adjustments",
                key: "adjustments",
                rows: [
                    {
                        label: "Property rights conveyed",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustments.propertyRightsConveyed.value",
                                    options: {
                                        type: "dropdown",
                                        options: [
                                            { label: "Fee Simple", value: "Fee Simple" },
                                            { label: "Lease Fee", value: "Lease Fee" },
                                            { label: "Leasehold", value: "Leasehold" },
                                        ],
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.propertyRightsConveyed.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.propertyRightsConveyed.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Financing",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustments.financing.value",
                                    options: {
                                        type: "dropdown",
                                        options: [
                                            { label: "Conventional", value: "Conventional" },
                                            {
                                                label: "Special Financing",
                                                value: "Special Financing",
                                            },
                                        ],
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.financing.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.financing.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Conditions of Sale",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustments.conditionsOfSale.value",
                                    options: {
                                        type: "dropdown",
                                        options: [
                                            { label: "Typical", value: "Typical" },
                                            { label: "Arms Length", value: "Arms Length" },
                                        ],
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.conditionsOfSale.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.conditionsOfSale.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Expenditures After Sale",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustments.expenditureAfterSale.value",
                                    options: {
                                        type: "dropdown",
                                        options: [{ label: "None", value: "None" }],
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.expenditureAfterSale.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.expenditureAfterSale.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Market Conditions",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustments.marketConditions.value",
                                    options: {
                                        type: "dropdown",
                                        options: [{ label: "Time", value: "Time" }],
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.marketConditions.rate",
                                    options: {
                                        type: "inputNumber",
                                        suffix: "%",
                                        editable: true,
                                        withSubject: false,
                                    },
                                },
                                {
                                    path: "adjustments.marketConditions.adjustedPrice",
                                    options: {
                                        type: "text",
                                        font: "b100",
                                        editable: false,
                                        prefix: "Adjusted price: $",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
            {
                title: "Other Adjustments",
                key: "otherAdjustments",
                rows: [
                    {
                        label: "Location",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.location",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Size",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.size",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Age/Condition",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.ageCondition",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Quality/Functional utility",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.qualityFunctionalUtility",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Office/Finished area",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.officeFinishedArea",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Land to building ratio",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.landToBuildingRatio",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Story height",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.storyHeight",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Other adjustments",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.otherAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Net adjustments",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.netAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Gross adjustments",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "otherAdjustments.grossAdjustments",
                                    options: {
                                        type: "inputNumber",
                                        editable: false,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Adjusted price / sqft",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "adjustedPricePerSqFt",
                                    options: {
                                        type: "text",
                                        editable: false,
                                        prefix: "$",
                                        font: "h100",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                    {
                        label: "Weight",
                        cells: generateCells(
                            data,
                            [
                                {
                                    path: "weight",
                                    options: {
                                        type: "inputNumber",
                                        editable: true,
                                        suffix: "%",
                                        withSubject: false,
                                    },
                                },
                            ],
                            size
                        ),
                    },
                ],
            },
        ],
    };
};
